import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { ProvinceList } from './ProvinceList';
import { AreaList } from './AreaList';
import { SiteList } from './SiteList';
import { OvertimeCostProvinceList } from './OvertimeCostProvinceList';
import { OvertimeCostAreaList } from './OvertimeCostAreaList';
import { OvertimeCostSiteList } from './OvertimeCostSiteList';
import { GuardsOverThresholdList } from './GuardsOverThresholdList';
import { UnderUtilisedGuardsList } from './UnderUtilisedGuardsList';
import { ActionLog } from './ActionLog';
import { DashboardView } from './DashboardView';
import { AllSitesView } from './AllSitesView';
import { AllGuardsView } from './AllGuardsView';
export function Layout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activePage, setActivePage] = useState<'dashboard' | 'action-log' | 'sites-optimisation' | 'overtime-cost' | 'guards-threshold' | 'guards-underutilised' | 'all-sites' | 'all-guards'>('dashboard');
  // State for drill-down navigation
  const [selectedProvince, setSelectedProvince] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedArea, setSelectedArea] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<'current' | 'next'>('current');
  // State for Action Log filters when navigating from dashboard
  const [actionLogStatusFilter, setActionLogStatusFilter] = useState<string | null>(null);
  const handleMetricClick = (action?: string) => {
    if (action === 'sites-optimisation') {
      setActivePage('sites-optimisation');
      setSelectedProvince(null); // Reset selection when entering fresh
      setSelectedArea(null);
    } else if (action === 'overtime-cost') {
      setActivePage('overtime-cost');
      setSelectedProvince(null); // Reset selection when entering fresh
      setSelectedArea(null);
    } else if (action === 'guards-threshold') {
      setActivePage('guards-threshold');
    } else if (action === 'guards-underutilised') {
      setActivePage('guards-underutilised');
    } else if (action === 'open-actions') {
      setActivePage('action-log');
      setActionLogStatusFilter('Open');
    }
  };
  // Helper to get province name from ID
  const getProvinceName = (id: string) => {
    const names: Record<string, string> = {
      wc: 'Western Cape',
      kzn: 'KwaZulu-Natal',
      gp: 'Gauteng'
    };
    return names[id] || id;
  };
  // Helper to get area name from ID
  const getAreaName = (id: string) => {
    const names: Record<string, string> = {
      'ct-cbd': 'Cape Town CBD',
      north: 'Northern Suburbs',
      south: 'Southern Suburbs',
      'dbn-central': 'Durban Central',
      umhlanga: 'Umhlanga',
      sandton: 'Sandton',
      midrand: 'Midrand'
    };
    return names[id] || id;
  };
  const renderContent = () => {
    switch (activePage) {
      case 'all-sites':
        return <AllSitesView data-id="element-800" />;
      case 'all-guards':
        return <AllGuardsView data-id="element-801" />;
      case 'guards-threshold':
        return <GuardsOverThresholdList onBack={() => setActivePage('dashboard')} data-id="element-802" />;
      case 'guards-underutilised':
        return <UnderUtilisedGuardsList onBack={() => setActivePage('dashboard')} data-id="element-803" />;
      case 'sites-optimisation':
        // Level 3: Site List (Deepest level)
        if (selectedProvince && selectedArea) {
          return <SiteList provinceName={selectedProvince.name} provinceId={selectedProvince.id} areaName={selectedArea.name} areaId={selectedArea.id} onBack={() => setSelectedArea(null)} data-id="element-804" />;
        }
        // Level 2: Area List
        if (selectedProvince) {
          return <AreaList provinceName={selectedProvince.name} provinceId={selectedProvince.id} onBack={() => setSelectedProvince(null)} onAreaClick={areaId => setSelectedArea({
            id: areaId,
            name: getAreaName(areaId)
          })} data-id="element-805" />;
        }
        // Level 1: Province List (Root level)
        return <ProvinceList onBack={() => setActivePage('dashboard')} onProvinceClick={id => setSelectedProvince({
          id,
          name: getProvinceName(id)
        })} data-id="element-806" />;
      case 'overtime-cost':
        // Level 3: Site List (Deepest level)
        if (selectedProvince && selectedArea) {
          return <OvertimeCostSiteList provinceName={selectedProvince.name} provinceId={selectedProvince.id} areaName={selectedArea.name} areaId={selectedArea.id} onBack={() => setSelectedArea(null)} data-id="element-807" />;
        }
        // Level 2: Area List
        if (selectedProvince) {
          return <OvertimeCostAreaList provinceName={selectedProvince.name} provinceId={selectedProvince.id} onBack={() => setSelectedProvince(null)} onAreaClick={areaId => setSelectedArea({
            id: areaId,
            name: getAreaName(areaId)
          })} data-id="element-808" />;
        }
        // Level 1: Province List (Root level)
        return <OvertimeCostProvinceList onBack={() => setActivePage('dashboard')} onProvinceClick={id => setSelectedProvince({
          id,
          name: getProvinceName(id)
        })} data-id="element-809" />;
      case 'action-log':
        return <ActionLog selectedMonth={selectedMonth} initialStatusFilter={actionLogStatusFilter} onClearFilters={() => setActionLogStatusFilter(null)} data-id="element-810" />;
      case 'dashboard':
      default:
        return <DashboardView onNavigate={handleMetricClick} data-id="element-811" />;
    }
  };
  return <div className="flex min-h-screen w-full bg-[#fafaf9]" data-id="element-812">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} activePage={activePage === 'sites-optimisation' || activePage === 'guards-threshold' || activePage === 'guards-underutilised' || activePage === 'overtime-cost' ? 'dashboard' : activePage} onNavigate={page => {
      setActivePage(page as any);
      // Reset filters when navigating via sidebar
      if (page === 'action-log') {
        setActionLogStatusFilter(null);
      }
    }} data-id="element-813" />

      {/* Main Content Wrapper */}
      <div className="flex flex-1 flex-col min-w-0" data-id="element-814">
        <Header onMenuClick={() => setIsSidebarOpen(true)} selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} data-id="element-815" />

        {/* Main Content Area */}
        <main className="flex-1 p-4 md:p-8 overflow-y-auto pb-safe" data-id="element-816">
          <div className="mx-auto max-w-7xl" data-id="element-817">
            {/* Page Header - Only show on main pages, not dashboard or drill-down */}
            {activePage !== 'dashboard' && activePage !== 'sites-optimisation' && activePage !== 'guards-threshold' && activePage !== 'guards-underutilised' && activePage !== 'overtime-cost' && <div className="mb-8" data-id="element-818">
                  <h1 className="text-2xl md:text-3xl font-bold text-[#062133]" data-id="element-819">
                    {activePage === 'dashboard' ? 'Dashboard' : activePage === 'action-log' ? 'Action Log' : activePage === 'all-sites' ? 'All Sites' : activePage === 'all-guards' ? 'All Guards' : ''}
                  </h1>
                  <p className="mt-2 text-[#5a7280]" data-id="element-820">
                    {activePage === 'action-log' && actionLogStatusFilter === 'Open' ? <span data-id="element-821">
                        Viewing{' '}
                        <span className="font-medium text-[#062133]" data-id="element-822">
                          Open Actions
                        </span>
                      </span> : activePage === 'all-sites' || activePage === 'all-guards' ? <span data-id="element-823">
                        Manage and filter your{' '}
                        {activePage === 'all-sites' ? 'sites' : 'guards'}
                      </span> : <span data-id="element-824">
                        Viewing data for{' '}
                        <span className="font-medium text-[#062133]" data-id="element-825">
                          {selectedMonth === 'current' ? 'February 2026' : 'March 2026'}
                        </span>
                      </span>}
                  </p>
                </div>}

            {/* Dynamic Content */}
            {renderContent()}
          </div>
        </main>
      </div>
    </div>;
}